import { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-aria-components/Button';
import { DialogTrigger } from 'react-aria-components/Dialog';
import { usePreferences } from '../stores/preferences';
import { AccessibleDialog } from './AccessibleDialog';
import { useAnnouncer } from './Announcer';

export function AccessibilityToolbar() {
  const { announce } = useAnnouncer();
  const [open, setOpen] = useState(false);

  const fontScale = usePreferences((state) => state.fontScale);
  const theme = usePreferences((state) => state.theme);
  const reduceMotion = usePreferences((state) => state.reduceMotion);
  const setFontScale = usePreferences((state) => state.setFontScale);
  const toggleThemePreference = usePreferences((state) => state.toggleTheme);
  const setReduceMotion = usePreferences((state) => state.setReduceMotion);

  const openPanel = useCallback(() => setOpen(true), []);

  useEffect(() => {
    const onShortcut = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.key.toLowerCase() !== 'a') return;
      event.preventDefault();
      if (open) document.getElementById('a11y-panel')?.focus();
      else openPanel();
    };
    document.addEventListener('keydown', onShortcut);
    return () => document.removeEventListener('keydown', onShortcut);
  }, [open, openPanel]);

  const changeFontScale = (value: number) => {
    setFontScale(value);
    announce(`Tamaño de texto: ${Math.round(usePreferences.getState().fontScale * 100)} por ciento.`);
  };

  const toggleTheme = () => {
    toggleThemePreference();
    announce(`Tema cambiado a ${usePreferences.getState().theme === 'dark' ? 'oscuro' : 'claro'}.`);
  };

  const toggleMotion = () => {
    const next = !reduceMotion;
    setReduceMotion(next);
    announce(`Movimiento reducido ${next ? 'activado' : 'desactivado'}.`);
  };

  return (
    <div className="position-relative">
      <DialogTrigger isOpen={open} onOpenChange={setOpen}>
        <Button
          className="btn btn-outline-primary a11y-toolbar-toggle"
          aria-expanded={open}
          aria-controls="a11y-panel"
          aria-keyshortcuts="Alt+A"
        >
          <i className="bi bi-universal-access-circle" aria-hidden="true" />
          <span className="d-none d-md-inline ms-1">Accesibilidad</span>
          <span className="visually-hidden d-md-none">Opciones de accesibilidad</span>
        </Button>

        <AccessibleDialog
          id="a11y-panel"
          isOpen={open}
          onOpenChange={setOpen}
          title="Opciones de accesibilidad"
          closeLabel="Cerrar opciones de accesibilidad"
          placement="end"
        >
              <div className="a11y-toolbar-row">
                <span id="a11y-font-label">Tamaño de texto</span>
                <div role="group" aria-labelledby="a11y-font-label" className="d-flex gap-2">
                  <Button className="a11y-btn" onPress={() => changeFontScale(fontScale - 0.1)} aria-label="Disminuir tamaño de texto">A−</Button>
                  <Button className="a11y-btn" onPress={() => changeFontScale(1)} aria-label="Restablecer tamaño de texto">A</Button>
                  <Button className="a11y-btn" onPress={() => changeFontScale(fontScale + 0.1)} aria-label="Aumentar tamaño de texto">A+</Button>
                </div>
              </div>

              <div className="a11y-toolbar-row">
                <span id="a11y-theme-label">Tema</span>
                <Button className="a11y-btn" onPress={toggleTheme} aria-pressed={theme === 'light'} aria-labelledby="a11y-theme-label">
                  <i className={`bi ${theme === 'dark' ? 'bi-moon-stars' : 'bi-sun'}`} aria-hidden="true" />
                  <span>{theme === 'dark' ? 'Oscuro' : 'Claro'}</span>
                </Button>
              </div>

              <div className="a11y-toolbar-row a11y-toolbar-shortcuts">
                <h3 className="h6 mb-1">Atajos de teclado</h3>
                <dl className="mb-0">
                  <div><dt>Alt + A</dt><dd>Estas opciones</dd></div>
                  <div><dt>Alt + M</dt><dd>El menú principal</dd></div>
                </dl>
              </div>

              <div className="a11y-toolbar-row">
                <span id="a11y-motion-label">Reducir movimiento</span>
                <Button className="a11y-btn" onPress={toggleMotion} aria-pressed={reduceMotion} aria-labelledby="a11y-motion-label">
                  {reduceMotion ? 'Activado' : 'Desactivado'}
                </Button>
              </div>
        </AccessibleDialog>
      </DialogTrigger>
    </div>
  );
}
